import Vue from 'vue'
import Router from 'vue-router'
import Store from '@/store'

import Billing from '@/components/billings/Billing'
import Billings from '@/components/billings/Billings'
import Calendar from '@/components/transactions/Calendar'
import ChangePassword from '@/components/ChangePassword'
import Chargeback from '@/components/transactions/Chargeback'
import Chargebacks from '@/components/transactions/Chargebacks'
import ApiRequestsPage from '@/components/manage/ApiRequestsPage'
import ConvertCsv from '@/components/manage/utilities/ConvertCsv'
import ConvertExcel from '@/components/manage/utilities/ConvertExcel'
import EmailDecode from '@/components/manage/utilities/EmailDecode'
import EmailOutgoingPage from '@/components/manage/EmailOutgoingPage'
import Help from '@/components/Help'
import HppLinkManagement from '@/components/manage/HppLinkManagement'
import Login from '@/components/Login'
import Merchant from '@/components/manage/Merchant'
import MerchantEdit from '@/components/manage/MerchantEdit'
import MerchantsPage from '@/components/manage/MerchantsPage'
import OTPVerify from '@/components/OTPVerify'
import Permissions from '@/components/manage/Permissions'
import Plans from '@/components/Plans'
import PlanManage from '@/components/plans/Manage'
import PnxInstallments from '@/components/PnxInstallments'
import PnxInstallmentManage from '@/components/pnxInstallments/Manage'
import Profile from '@/components/Profile'
import Recurring from '@/components/Recurring'
import Reports from '@/components/manage/Reports'
import ResetPassword from '@/components/ResetPassword'
import AnalyticsDashboard from '@/components/charts/AnalyticsDashboard'
import StandaloneRefund from '@/components/manage/StandaloneRefund'
import Schedules from '@/components/Schedules'
import StringGenerator from '@/components/manage/utilities/StringGenerator'
import Terminal from '@/components/manage/Terminal'
import TerminalEdit from '@/components/manage/TerminalEdit'
import TerminalsPage from '@/components/manage/TerminalsPage'
import Tokenize from '@/components/terminal/Tokenize'
import Transactions from '@/components/Transactions'
import User from '@/components/manage/User'
import UserEdit from '@/components/manage/UserEdit'
import UsersPage from '@/components/manage/UsersPage'
import VirtualTerminal from '@/components/VirtualTerminal'

import { SPECIALIZED_TERMINALS } from '@/components/terminal/specialized'

import OkdmvAgencies from '@/components/manage/okdmv/Agencies'
import OkdmvAgency from '@/components/manage/okdmv/Agency'
import OkdmvAgencyEdit from '@/components/manage/okdmv/AgencyEdit'
import OkdmvIndex from '@/components/manage/okdmv/Okdmv'
import OkdmvRenewal from '@/components/manage/okdmv/Renewal'
import OkdmvRenewals from '@/components/manage/okdmv/Renewals'

import FpgMerchants from '@/components/manage/fpg/Merchants'
import FpgTransactionSearch from '@/components/manage/fpg/TransactionSearch'

import PaynetworxImport from '@/components/manage/paynetworx/ImportMerchant'
import PaynetworxFunding from '@/components/manage/paynetworx/Funding'

import TrxservicesQuery from '@/components/manage/trxservices/Query'

import NotFound from '@/components/NotFound'

Vue.use(Router)

function hasAccountPermission (permission, merchantKsuid = null) {
  const myProfile = Store.state.myProfile
  if (!myProfile || !myProfile.accounts) {
    return false
  }

  // If merchant is specified, check that specific account only
  if (merchantKsuid) {
    const account = myProfile.accounts.find(acc => acc.entity_ksuid === merchantKsuid)
    return account && account.user_permissions && account.user_permissions[permission]
  }

  // For route guards, check if ANY account has the permission
  // The backend API should filter data based on actual account access
  return myProfile.accounts.some(account => {
    return account.user_permissions && account.user_permissions[permission]
  })
}

function guard (to, from, next) {
  try {
    const token = Store.state.token
    const forceChangePassword = Store.state['force_change_password']
    const passwordGracePeriod = Store.state['password_grace_period']

    if (token) {
      const isAccountAdmin = Array.isArray(Store.state.permissions.account_admin) &&
        Store.state.permissions.account_admin.length > 0

      // Verify permissions
      const hasPermissionDenied = to.matched.some(record => {
        if (!record.meta.needsAuth || Store.state.permissions.superuser) {
          return false
        }
        // Routes flagged accountAdmin are open to scoped account admins;
        // the backend still enforces per-account access.
        if (record.meta.accountAdmin && isAccountAdmin) {
          return false
        }
        if (record.meta.permissions) {
          if (record.meta.accountPermission) {
            return !hasAccountPermission(record.meta.permissions)
          }
          // account_admin passes every user-permission gate except superuser
          // itself (mirrors hasPermission in the auth mixin — the guard reads
          // the store directly, so it needs its own bypass)
          if (isAccountAdmin && record.meta.permissions !== 'superuser') {
            return false
          }
          return !Store.state.permissions[record.meta.permissions]
        }
        return false
      })

      if (hasPermissionDenied) {
        Store.commit('clearErrors')
        Store.commit('error', 'Permission Denied')
        next(false)
      } else if (forceChangePassword && !passwordGracePeriod && to.path !== '/change_password') {
        // Force expired passwords to be reset.
        next({
          path: '/change_password'
        })
      } else {
        if (to.path === from.path) {
          // prevent redundant navigation
          next(false)
        } else {
          // Proceed as normal
          next()
        }
      }
    } else {
      if (to.path === from.path) {
        // prevent redundant navigation
        next(false)
      } else {
        next({
          path: '/',
          query: { redirect: to.fullPath }
        })
      }
    }
  } catch (err) {
    console.error('Error at Navigation Guard: ', err)
  }
}

function guardChangePassword (to, from, next) {
  try {
    const token = Store.state.token
    if (!token) {
      Store.commit('clearErrors')
      Store.commit('error', 'You must be logged in.')
      next({
        path: '/',
        query: { redirect: to.fullPath }
      })
    } else {
      next()
    }
  } catch (err) {
    console.err('Error at /change_password navigation', err)
  }
}

export default new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'Login',
      component: Login,
      meta: {
        needsAuth: false,
        title: 'Login'
      }
    },

    // User Routes
    {
      path: '/change_password',
      name: 'ChangePassword',
      component: ChangePassword,
      beforeEnter: guardChangePassword,
      meta: {
        needsAuth: true,
        title: 'Change Password'
      }
    },
    {
      path: '/reset_password/:token',
      name: 'ResetPassword',
      component: ResetPassword,
      meta: {
        needsAuth: false,
        title: 'Reset Password'
      }
    },
    {
      path: '/otp_verify',
      name: 'OTPVerify',
      component: OTPVerify,
      meta: {
        needsAuth: true,
        title: 'OTP Verify'
      }
    },
    {
      path: '/help',
      name: 'Help',
      component: Help,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        title: 'Help'
      }
    },
    {
      path: '/profile',
      name: 'Profile',
      component: Profile,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        title: 'Profile'
      }
    },

    // Payment Routes
    {
      path: '/credit',
      name: 'ChargeCard',
      component: VirtualTerminal,
      props: { paymentType: 'card' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_use_virtual_terminal',
        title: 'Terminal'
      }
    },
    {
      path: '/ach',
      name: 'ChargeACH',
      component: VirtualTerminal,
      props: { paymentType: 'ach' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_use_virtual_terminal',
        title: 'Terminal'
      }
    },
    {
      path: '/tokenize',
      name: 'TokenizeCard',
      component: Tokenize,
      props: { paymentType: 'card' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_use_virtual_terminal',
        title: 'Tokenize'
      }
    },
    {
      path: '/ach_tokenize',
      name: 'TokenizeACH',
      component: Tokenize,
      props: { paymentType: 'ach' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_use_virtual_terminal',
        title: 'Tokenize'
      }
    },

    // Specialized Terminal Routes (generated from registry)
    ...SPECIALIZED_TERMINALS.map(t => ({
      path: `/terminal/${t.slug}`,
      name: t.routeName,
      component: t.component,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: t.permission,
        title: t.displayName,
        slug: t.slug
      }
    })),

    // Transaction Routes
    {
      path: '/calendar',
      name: 'Calendar',
      component: Calendar,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        title: 'Calendar'
      }
    },

    {
      path: '/recurring',
      name: 'Recurring',
      component: Recurring,
      // props: { paymentType: 'card' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_view_transactions',
        title: 'Recurring'
      }
    },
    {
      path: '/ach_recurring',
      name: 'RecurringAch',
      component: Recurring,
      props: { paymentType: 'ach' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_view_transactions',
        title: 'Recurring'
      }
    },

    {
      path: '/schedules',
      name: 'Schedules',
      component: Schedules,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_view_transactions',
        title: 'Schedules'
      }
    },
    {
      path: '/ach_schedules',
      name: 'SchedulesAch',
      component: Schedules,
      props: { paymentType: 'ach' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_view_transactions',
        title: 'Schedules'
      }
    },

    {
      path: '/transactions',
      name: 'Transactions',
      component: Transactions,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_view_transactions',
        title: 'Transactions'
      }
    },
    {
      path: '/ach_transactions',
      name: 'TransactionsAch',
      component: Transactions,
      props: { paymentType: 'ach' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_view_transactions',
        title: 'Transactions'
      }
    },

    // Installment Plan Routes
    // @TODO Child Routes
    {
      path: '/plans',
      name: 'Plans',
      component: Plans,
      props: { paymentType: 'card' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'has_installment_options',
        title: 'Plans'
      }
    },
    {
      path: '/ach_plans',
      name: 'PlansAch',
      component: Plans,
      props: { paymentType: 'ach' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'has_installment_options',
        title: 'Plans'
      }
    },
    {
      path: '/plans/manage',
      name: 'PlanManage',
      component: PlanManage,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'has_installment_options',
        title: 'Manage Plan'
      }
    },

    // PNX Installment Routes
    {
      path: '/pnx_installments',
      name: 'PnxInstallments',
      component: PnxInstallments,
      props: { paymentType: 'card' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'has_installment_options',
        title: 'Installments'
      }
    },
    {
      path: '/ach_pnx_installments',
      name: 'PnxInstallmentsAch',
      component: PnxInstallments,
      props: { paymentType: 'ach' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'has_installment_options',
        title: 'Installments'
      }
    },
    {
      path: '/pnx_installments/manage',
      name: 'PnxInstallmentManage',
      component: PnxInstallmentManage,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'has_installment_options',
        title: 'Manage Installment'
      }
    },

    // Billing Routes
    {
      path: '/billings',
      name: 'Billings',
      component: Billings,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_use_billings',
        accountAdmin: true,
        title: 'Billings'
      }
    },
    {
      path: '/billings/:id',
      name: 'Billing',
      component: Billing,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_use_billings',
        accountAdmin: true,
        title: 'Billing'
      }
    },

    // Chargeback Routes
    // @TODO Child Routes, possibly with transactions in general
    {
      path: '/chargebacks',
      name: 'Chargebacks',
      component: Chargebacks,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_view_transactions',
        title: 'Chargebacks'
      }
    },
    {
      path: '/chargebacks/:id',
      name: 'Chargeback',
      component: Chargeback,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'Chargeback'
      }
    },

    // Management Routes
    // @TODO Child Routes
    {
      path: '/merchants',
      name: 'Merchants',
      component: MerchantsPage,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'Merchants'
      }
    },
    {
      path: '/merchants/add',
      name: 'MerchantAdd',
      component: MerchantEdit,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'Merchant Add'
      }
    },
    {
      path: '/merchants/:id',
      name: 'Merchant',
      component: Merchant,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'Merchant'
      }
    },
    {
      path: '/merchants/:id/edit',
      name: 'MerchantEdit',
      component: MerchantEdit,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'Merchant Edit'
      }
    },

    {
      path: '/admin/reports',
      name: 'Reports',
      component: Reports,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'Reports'
      }
    },

    {
      path: '/analytics',
      name: 'Analytics',
      component: AnalyticsDashboard,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        title: 'Analytics Dashboard'
      }
    },

    {
      path: '/admin/permissions',
      name: 'Permissions',
      component: Permissions,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'Permissions'
      }
    },

    {
      path: '/admin/email_outgoing',
      name: 'EmailOutgoing',
      component: EmailOutgoingPage,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'Email Outgoing'
      }
    },

    {
      path: '/admin/api_requests',
      name: 'ApiRequests',
      component: ApiRequestsPage,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'API Requests'
      }
    },

    {
      path: '/admin/hpp_links',
      name: 'HppLinkManagement',
      component: HppLinkManagement,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'HPP Link Management'
      }
    },

    {
      path: '/terminals',
      name: 'Terminals',
      component: TerminalsPage,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'Terminals'
      }
    },
    {
      path: '/terminals/add',
      name: 'TerminalAdd',
      component: TerminalEdit,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'Terminal Add'
      }
    },
    {
      path: '/terminals/:id',
      name: 'Terminal',
      component: Terminal,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'Terminal'
      }
    },

    {
      path: '/users',
      name: 'Users',
      component: UsersPage,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'Users'
      }
    },
    {
      path: '/users/add/:account_id?',
      name: 'UserAdd',
      component: UserEdit,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'User Add'
      }
    },
    {
      path: '/users/:id',
      name: 'User',
      component: User,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'User'
      }
    },
    { path: '/users/:id/edit', redirect: { name: 'User' } },

    // OKDMV Routes
    // @TODO Child Routes
    {
      path: '/okdmv',
      name: 'OkdmvIndex',
      component: OkdmvIndex,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'OKDMV'
      }
    },
    {
      path: '/okdmv/agencies',
      name: 'OkdmvAgencies',
      component: OkdmvAgencies,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'OKDMV Agencies'
      }
    },
    {
      path: '/okdmv/agencies/add',
      name: 'OkdmvAgencyAdd',
      component: OkdmvAgencyEdit,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'OKDMV Agency Add'
      }
    },
    {
      path: '/okdmv/agencies/:id',
      name: 'OkdmvAgency',
      component: OkdmvAgency,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'OKDMV Agency'
      }
    },
    {
      path: '/okdmv/agencies/:id/edit',
      name: 'OkdmvAgencyEdit',
      component: OkdmvAgencyEdit,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'OKDMV Agency Edit'
      }
    },
    {
      path: '/renewal',
      name: 'Renewal',
      redirect: to => ({
        name: 'OkdmvRenewal',
        params: { id: to.query.id },
        query: {}
      })
    },
    {
      path: '/okdmv/renewal/:id',
      name: 'OkdmvRenewal',
      component: OkdmvRenewal,
      props: { paymentType: 'card' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_use_okdmv_renewal',
        title: 'Renewal'
      }
    },
    {
      path: '/okdmv/renewals',
      name: 'OkdmvRenewals',
      component: OkdmvRenewals,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_use_okdmv_renewal',
        title: 'OKDMV Renewals'
      }
    },
    {
      path: '/okdmv/renewals/:id',
      name: 'OkdmvRenewalDetail',
      component: OkdmvRenewal,
      props: { paymentType: 'card' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_use_okdmv_renewal',
        title: 'Renewal'
      }
    },

    // FPG Routes
    // @TODO Child Routes
    {
      path: '/fpg/merchants',
      name: 'FpgMerchants',
      component: FpgMerchants,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'FPG Merchants'
      }
    },
    {
      path: '/fpg/search',
      name: 'FpgSearch',
      component: FpgTransactionSearch,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'FPG Search'
      }
    },

    // Paynetworx Routes
    {
      path: '/paynetworx/import',
      name: 'PaynetworxImport',
      component: PaynetworxImport,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'Import Merchant'
      }
    },
    {
      path: '/paynetworx/funding',
      name: 'PaynetworxFunding',
      component: PaynetworxFunding,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'can_view_funding',
        accountPermission: true,
        title: 'Settlement & Funding'
      }
    },

    // Trxservices Routes
    {
      path: '/trxservices/query',
      name: 'TrxservicesQuery',
      component: TrxservicesQuery,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'General Query'
      }
    },

    // Utility Routes
    // @TODO Child Routes, maybe not in "admin"
    {
      path: '/admin/refund',
      name: 'StandaloneRefund',
      component: StandaloneRefund,
      props: { paymentType: 'card' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'Standalone Refund'
      }
    },
    {
      path: '/admin/ach_refund',
      name: 'StandaloneRefundACH',
      component: StandaloneRefund,
      props: { paymentType: 'ach' },
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        accountAdmin: true,
        title: 'Standalone Refund'
      }
    },
    {
      path: '/admin/generator',
      name: 'StringGenerator',
      component: StringGenerator,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'Sting Generator'
      }
    },
    {
      path: '/admin/convertcsv',
      name: 'ConvertCsv',
      component: ConvertCsv,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'Convert CSV'
      }
    },
    {
      path: '/admin/convertexcel',
      name: 'ConvertExcel',
      component: ConvertExcel,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'Convert Excel'
      }
    },
    {
      path: '/admin/emaildecode',
      name: 'EmailDecode',
      component: EmailDecode,
      beforeEnter: guard,
      meta: {
        needsAuth: true,
        permissions: 'superuser',
        title: 'Email Decode'
      }
    },

    {
      path: '*',
      component: NotFound
    }
  ]
})



// WEBPACK FOOTER //
// ./src/router/index.js